import { FC, ReactElement, createContext, useEffect, useMemo } from 'react';
import useOrderFlow from '../hooks/order/useOrderFlow';
import usePusher from '../hooks/global/usePusher';
import useFocus from '../hooks/utility/useFocus';
import { orderTrackingChannel } from '../services/pusher/Channels';
import { ORDER_FULFILLMENT_STATUSES, ORDER_METHODS, PUSHER_EVENTS } from '../services/exports/Constants';
import { Order } from '../services/exports/Interfaces';
import { useTranslation } from 'react-i18next';
import useAppMethods from '../hooks/utility/useAppMethods';
import useNotification from '../hooks/utility/useNotification';
import { ToastOptions } from 'react-toastify';

interface Props {
  children: ReactElement;
}

interface IOrderStatusContext {
  disconnect: () => void;
}

export const OrderStatusContext = createContext<IOrderStatusContext>({
  disconnect: () => {},
});

const OrderStatusProvider: FC<Props> = ({ children }): ReactElement => {
  const { t } = useTranslation(null, { keyPrefix: 'Providers:OrderStatusProvider' });
  const { ongoingOrder, refreshOngoingOrder } = useOrderFlow();
  const { connect, disconnect } = usePusher();
  const { formatTime } = useAppMethods();
  const { notify } = useNotification();

  useFocus(
    () => initData(),
    () => disconnect(),
  );

  useEffect(() => {
    return () => disconnect();
  }, []);

  async function initData() {
    refreshOngoingOrder();
    connectPusher();
  }

  const getToastConfig = (data: Partial<Order>): { message: string; type?: ToastOptions['type'] } => {
    if (ongoingOrder?.fulfillment.status === data.fulfillment.status) {
      return {
        message: t('order_status_toast.order_preparation_up_time_changed', {
          number: data.fulfillment.number,
          type: data.method === ORDER_METHODS.PICKUP ? t('order_type.pickup') : t('order_type.delivery'),
          time: formatTime(data.fulfillment.estimated_pickup_time),
        }),
      };
    }

    switch (data.fulfillment?.status) {
      case ORDER_FULFILLMENT_STATUSES.REVIEWED:
        return {
          message: t('order_status_toast.order_reviewed', {
            number: data.fulfillment.number,
          }),
        };
      case ORDER_FULFILLMENT_STATUSES.ACCEPTED:
        return {
          message: t('order_status_toast.order_confirmed', {
            number: data.fulfillment.number,
            time: formatTime(data.fulfillment.estimated_pickup_time),
          }),
          type: 'success',
        };
      case ORDER_FULFILLMENT_STATUSES.PREPARING:
        return {
          message: t('order_status_toast.order_preparing', {
            number: data.fulfillment.number,
          }),
        };
      case ORDER_FULFILLMENT_STATUSES.REJECTED:
        return {
          message: t('order_status_toast.order_rejected', {
            number: data.fulfillment.number,
          }),
          type: 'error',
        };
      default:
        return {
          message: t('order_status_toast.order_preparation_up_time_changed', {
            number: data.fulfillment.number,
            type: data.method === ORDER_METHODS.PICKUP ? t('order_type.pickup') : t('order_type.delivery'),
            time: formatTime(data.fulfillment.estimated_pickup_time),
          }),
        };
    }
  };

  const orderUpdatedHandler = (data: Partial<Order>): void => {
    if (
      data.fulfillment.status !== ongoingOrder.fulfillment.status ||
      data.fulfillment.estimated_pickup_time !== ongoingOrder.fulfillment.estimated_pickup_time
    ) {
      const config = getToastConfig(data);
      notify({ ...config });
    }

    refreshOngoingOrder();
  };

  async function connectPusher() {
    if (!ongoingOrder?.id) return;

    const pusher = await connect();

    pusher.subscribe(orderTrackingChannel(ongoingOrder.uuid)).bind(PUSHER_EVENTS.ORDER_UPDATED, orderUpdatedHandler);
  }

  const context = useMemo(() => ({ disconnect }), []);

  return <OrderStatusContext.Provider value={context}>{children}</OrderStatusContext.Provider>;
};

export default OrderStatusProvider;
